import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  X,
  Plus,
  ChevronDown,
  Check,
  CircleDot,
  FileUp,
  AlignLeft,
  CheckCircle2,
  XCircle,
  Upload,
  ImageIcon,
  AlertCircle,
  SquareCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Textarea } from "src/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group";
import { Label } from "src/components/ui/label";
import { Input } from "src/components/ui/input";
import { QuestionType } from "src/lib/types";
import { cn, generateId } from "src/lib/utils";
import OptionsList from "./options-list";
import axios from "axios";
import { toast } from "sonner";
import { Avatar, Progress, Tooltip } from "antd";
import { BaseUrlApi, UrlImage } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

export default function QuestionCard({
  question,
  updateQuestion,
  removeQuestion,
  isEditMode,
  isShowMode,
  selectedAnswer,
  onAnswerSelect,
  showCorrectAnswers = false,
  disabled = false,
  fileName = "questions",
  isSurvey = false,
}) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [score, setScore] = useState(
    question?.score_answer || (isEditMode && "5.00") || "0.00"
  );
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const uploadStartTime = useRef(null);
  const uploadSpeeds = useRef([]);

  const [uploadState, setUploadState] = useState({
    file: null,
    preview: null,
    uploading: false,
    progress: 0,
    remainingTime: null,
    uploadedUrl: question?.imageUrl || null,
    error: null,
  });

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: question?.id });

  const style = { transform: CSS.Transform.toString(transform), transition };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (uploadState.preview) URL.revokeObjectURL(uploadState.preview);
    };
  }, [uploadState.preview]);

  const handleTextChange = (text) => updateQuestion({ ...question, text });

  const handleScoreChange = (e) => {
    const newScore = e.target.value;
    setScore(newScore);
    if (isEditMode || +newScore <= +question?.score) {
      updateQuestion({ ...question, score: newScore });
    } else {
      toast(language === "ar" ? "تحذير !!" : "Warning !!", {
        description:
          language === "ar"
            ? `يجب أن تكون النتيجة أصغر من أو تساوي ${question?.score}`
            : `The score should be smaller than or equal ${question?.score}`,
      });
    }
  };

  const handleOptionChange = (optionId, text) => {
    const updatedOptions = question?.options.map((option) =>
      option.id === optionId ? { ...option, text } : option
    );
    updateQuestion({ ...question, options: updatedOptions });
  };

  const handleCorrectOptionChange = (optionId) => {
    const updatedOptions = question?.options.map((option) => ({
      ...option,
      isCorrect: option.id === optionId,
    }));
    updateQuestion({ ...question, options: updatedOptions });
  };

  const addOption = () => {
    const newOption = {
      id: generateId(),
      text: "",
      isCorrect: isSurvey ? undefined : false,
    };
    updateQuestion({ ...question, options: [...question?.options, newOption] });
  };

  const removeOption = (optionId) => {
    const updatedOptions = question?.options.filter(
      (option) => option.id !== optionId
    );
    updateQuestion({ ...question, options: updatedOptions });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const calculateRemainingTime = (loaded, total) => {
    if (!uploadStartTime.current) {
      uploadStartTime.current = Date.now();
      return null;
    }
    const elapsedTime = (Date.now() - uploadStartTime.current) / 1000;
    const uploadSpeed = loaded / elapsedTime;
    uploadSpeeds.current.push(uploadSpeed);
    if (uploadSpeeds.current.length > 5) uploadSpeeds.current.shift();
    const avgSpeed =
      uploadSpeeds.current.reduce((sum, speed) => sum + speed, 0) /
      uploadSpeeds.current.length;
    const remainingBytes = total - loaded;
    return Math.round(remainingBytes / avgSpeed);
  };

  const handleUpload = async (file) => {
    uploadStartTime.current = null;
    uploadSpeeds.current = [];
    setUploadState((prev) => ({
      ...prev,
      file,
      preview: URL.createObjectURL(file),
      uploading: true,
      progress: 0,
      remainingTime: null,
      error: null,
    }));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", fileName);

    try {
      const response = await axios.post(`${BaseUrlApi}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            const remainingTime = calculateRemainingTime(
              progressEvent.loaded,
              progressEvent.total
            );
            setUploadState((prev) => ({
              ...prev,
              progress: percentCompleted,
              remainingTime,
            }));
          }
        },
      });

      if (response.data && response.data.url) {
        setUploadState((prev) => ({
          ...prev,
          uploading: false,
          progress: 100,
          uploadedUrl: response.data.url,
        }));
        updateQuestion({ ...question, imageUrl: response.data.url });
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadState((prev) => ({
        ...prev,
        uploading: false,
        error: "Upload failed. Please try again.",
      }));
      toast("Upload Failed", { description: "Please try again later." });
    }
  };

  const removeImage = () => {
    setUploadState({
      file: null,
      preview: null,
      uploading: false,
      progress: 0,
      remainingTime: null,
      uploadedUrl: null,
      error: null,
    });
    updateQuestion({ ...question, imageUrl: null });
  };

  const handleOptionReorder = (reorderedOptions) =>
    updateQuestion({ ...question, options: reorderedOptions });

  const changeQuestionType = (type) => {
    let updatedOptions = [...question?.options];
    if (isSurvey) {
      if (
        type === QuestionType.SINGLE_CHOICE ||
        type === QuestionType.SURVEY_MULTIPLE_CHOICE
      ) {
        updatedOptions = updatedOptions.map(({ id, text }) => ({ id, text }));
        if (updatedOptions.length === 0) {
          updatedOptions = [
            { id: generateId(), text: "" },
            { id: generateId(), text: "" },
          ];
        }
      }
    } else {
      if (type === QuestionType.TRUE_FALSE) {
        updatedOptions = [
          {
            id: generateId(),
            text: language === "ar" ? "صح" : "True",
            isCorrect: true,
          },
          {
            id: generateId(),
            text: language === "ar" ? "خطأ" : "False",
            isCorrect: false,
          },
        ];
      } else if (
        type === QuestionType.MULTIPLE_CHOICE &&
        updatedOptions.length === 0
      ) {
        updatedOptions = [
          { id: generateId(), text: "", isCorrect: false },
          { id: generateId(), text: "", isCorrect: false },
        ];
      } else if (
        (type === QuestionType.PARAGRAPH ||
          type === QuestionType.FILE_UPLOAD) &&
        (question?.type === QuestionType.MULTIPLE_CHOICE ||
          question?.type === QuestionType.TRUE_FALSE)
      ) {
        updatedOptions = [];
      }
    }
    updateQuestion({ ...question, type, options: updatedOptions });
    setIsDropdownOpen(false);
  };

  const getQuestionTypeLabel = () => {
    switch (question?.type) {
      case QuestionType.SINGLE_CHOICE:
        return language === "ar" ? "اختيار واحد" : "Single Choice";
      case QuestionType.SURVEY_MULTIPLE_CHOICE:
        return language === "ar" ? "اختيار متعدد" : "Multiple Choice";
      case QuestionType.MULTIPLE_CHOICE:
        return language === "ar" ? "اختيار من متعدد" : "Multiple Choice";
      case QuestionType.TRUE_FALSE:
        return language === "ar" ? "صواب أو خطأ" : "True/False";
      case QuestionType.PARAGRAPH:
        return language === "ar" ? "فقرة" : "Paragraph";
      case QuestionType.FILE_UPLOAD:
        return language === "ar" ? "تحميل ملف" : "File Upload";
      default:
        return "";
    }
  };

  const getQuestionTypeIcon = () => {
    switch (question?.type) {
      case QuestionType.SINGLE_CHOICE:
        return <CircleDot className="h-5 w-5" />;
      case QuestionType.SURVEY_MULTIPLE_CHOICE:
        return <SquareCheck className="h-5 w-5" />;
      case QuestionType.MULTIPLE_CHOICE:
        return <CircleDot className="h-5 w-5" />;
      case QuestionType.TRUE_FALSE:
        return (
          <div className="flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-green-600 mr-0.5" />
            <XCircle className="h-4 w-4 text-red-600" />
          </div>
        );
      case QuestionType.PARAGRAPH:
        return <AlignLeft className="h-5 w-5" />;
      case QuestionType.FILE_UPLOAD:
        return <FileUp className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const handleParagraphAnswerChange = (e) =>
    updateQuestion({ ...question, answer: e.target.value });

  return (
    <Card
      ref={isEditMode ? setNodeRef : undefined}
      style={isEditMode ? style : undefined}
      className="mb-4 relative overflow-hidden"
      dir="ltr"
    >
      <div className="absolute top-0 bottom-0 right-0 w-1 bg-blue-600"></div>
      {!isShowMode && isEditMode ? (
        <CardHeader className="flex flex-row items-center justify-between p-4 pb-0">
          <div
            className="flex items-center justify-between gap-2 relative w-full"
            ref={dropdownRef}
          >
            <div className="flex items-center gap-2">
              <div {...attributes} {...listeners} className="cursor-grab">
                <GripVertical className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  disabled={disabled}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors"
                >
                  <span className="font-medium">{getQuestionTypeLabel()}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-1 w-40 shadow-lg bg-background rounded-md border z-50"
                    >
                      <div className="py-1">
                        {isSurvey ? (
                          <>
                            <button
                              onClick={() =>
                                changeQuestionType(QuestionType.SINGLE_CHOICE)
                              }
                              className="flex items-center gap-2 w-full px-4 py-2 text-right"
                            >
                              <CircleDot className="h-5 w-5" />
                              <span>
                                {language === "ar"
                                  ? "اختيار واحد"
                                  : "Single Choice"}
                              </span>
                            </button>
                            <button
                              onClick={() =>
                                changeQuestionType(
                                  QuestionType.SURVEY_MULTIPLE_CHOICE
                                )
                              }
                              className="flex items-center gap-2 w-full px-4 py-2 text-right"
                            >
                              <SquareCheck className="h-5 w-5" />
                              <span>
                                {language === "ar"
                                  ? "اختيار متعدد"
                                  : "Multiple Choice"}
                              </span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                changeQuestionType(QuestionType.MULTIPLE_CHOICE)
                              }
                              className="flex items-center gap-2 w-full px-4 py-2 text-right"
                            >
                              <CircleDot className="h-5 w-5" />
                              <span>
                                {language === "ar"
                                  ? "اختيار من متعدد"
                                  : "Multiple Choice"}
                              </span>
                            </button>
                            <button
                              onClick={() =>
                                changeQuestionType(QuestionType.TRUE_FALSE)
                              }
                              className="flex items-center gap-2 w-full px-4 py-2 text-right"
                            >
                              <div className="flex items-center justify-center">
                                <CheckCircle2 className="h-4 w-4 text-green-600 mr-0.5" />
                                <XCircle className="h-4 w-4 text-red-600" />
                              </div>
                              <span>
                                {language === "ar"
                                  ? "صواب أو خطأ"
                                  : "True/False"}
                              </span>
                            </button>
                            <button
                              onClick={() =>
                                changeQuestionType(QuestionType.PARAGRAPH)
                              }
                              className="flex items-center gap-2 w-full px-4 py-2 text-right"
                            >
                              <AlignLeft className="h-5 w-5" />
                              <span>
                                {language === "ar" ? "فقرة" : "Paragraph"}
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isSurvey && (
                <>
                  <label
                    htmlFor={`score-${question?.id}`}
                    className="text-sm whitespace-nowrap"
                  >
                    {language === "ar" ? "الدرجة:" : "Score:"}
                  </label>
                  <Input
                    id={`score-${question?.id}`}
                    type="number"
                    min="0"
                    step="0.5"
                    value={score}
                    onChange={handleScoreChange}
                    max={question?.score}
                    className="appearance-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
                    style={{ MozAppearance: "textfield" }}
                    disabled={disabled}
                  />
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeQuestion(question?.id)}
                className="min-h-8 min-w-8"
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
      ) : null}
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              {isEditMode ? (
                <Textarea
                  placeholder={
                    language === "ar"
                      ? "من فضلك اكتب السؤال هنا!"
                      : "Please write your question here!"
                  }
                  value={question?.text}
                  onChange={(e) => handleTextChange(e.target.value)}
                  className="resize-none"
                  disabled={disabled}
                />
              ) : (
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="text-lg font-medium">{question?.text}</div>
                  {!isSurvey && (
                    <div className="flex items-center gap-2">
                      {user?.role === "teacher" &&
                        question?.type === QuestionType.PARAGRAPH && (
                          <div className="flex items-center gap-2">
                            <label
                              htmlFor={`score-${question?.id}`}
                              className="text-sm whitespace-nowrap"
                            >
                              {language === "ar" ? "الدرجة:" : "Score:"}
                            </label>
                            <Input
                              id={`score-${question?.id}`}
                              type="number"
                              min="0"
                              step="0.5"
                              value={score}
                              onChange={handleScoreChange}
                              max={question?.score}
                              className="appearance-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
                              style={{ MozAppearance: "textfield" }}
                              disabled={disabled || !question.score_answer}
                            />
                            /
                          </div>
                        )}
                      {question?.score_answer && user?.role === "student"
                        ? `${question?.score_answer} /`
                        : null}
                      <span className="font-semibold">{question?.score}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {isEditMode && !isSurvey && (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "h-10 w-10",
                    uploadState.uploading && "bg-blue-50 text-blue-600"
                  )}
                  disabled={disabled || uploadState.uploading}
                >
                  {uploadState.uploading ? (
                    <Upload className="h-4 w-4 animate-pulse" />
                  ) : (
                    <ImageIcon className="h-4 w-4" />
                  )}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                  disabled={disabled || uploadState.uploading}
                />
              </div>
            )}
          </div>

          {uploadState.uploading && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-blue-50 rounded-md p-3"
            >
              <div className="flex justify-between items-center mb-1">
                <div className="text-sm font-medium text-blue-700">
                  {language === "ar"
                    ? "جاري تحميل الصورة..."
                    : "Uploading image..."}
                </div>
                <div className="text-sm text-blue-700">
                  {uploadState.remainingTime &&
                    ` • ${uploadState.remainingTime} ${
                      language === "ar" ? "متبقي" : "remaining"
                    }`}
                </div>
              </div>
              <Progress percent={uploadState.progress} className="h-2" />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-blue-600"></div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-100 p-0 px-2"
                  onClick={removeImage}
                >
                  {language === "ar" ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </motion.div>
          )}

          {uploadState.error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-50 rounded-md p-3 flex items-center gap-2"
            >
              <AlertCircle className="h-4 w-4 text-red-600" />
              <div className="text-sm text-red-700">{uploadState.error}</div>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 text-xs text-red-700 hover:text-red-800 hover:bg-red-100 p-0 px-2"
                onClick={() =>
                  setUploadState((prev) => ({ ...prev, error: null }))
                }
              >
                {language === "ar" ? "إغلاق" : "Dismiss"}
              </Button>
            </motion.div>
          )}

          {(uploadState.uploadedUrl || uploadState.preview) &&
            !uploadState.uploading &&
            !uploadState.error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="relative"
              >
                <img
                  src={`${UrlImage}${
                    uploadState.uploadedUrl || uploadState.preview
                  }`}
                  alt="Question image"
                  className="max-h-40 rounded-md object-contain"
                />
                {isEditMode && (
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={removeImage}
                    className="absolute top-2 right-2 h-6 w-6"
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </motion.div>
            )}

          {question.file && (
            <img
              src={`${UrlImage}${question.file}`}
              alt="Question image"
              className="max-h-40 rounded-md object-contain"
            />
          )}

          {(question?.type === QuestionType.SINGLE_CHOICE ||
            question?.type === QuestionType.SURVEY_MULTIPLE_CHOICE ||
            question?.type === QuestionType.MULTIPLE_CHOICE) && (
            <div className="space-y-2">
              {isEditMode ? (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-500">
                      {language === "ar" ? "الخيارات" : "Options"}
                    </div>
                    {!isSurvey && (
                      <div className="text-sm font-medium text-green-600">
                        {language === "ar"
                          ? "الإجابة الصحيحة"
                          : "Correct Answer"}
                      </div>
                    )}
                  </div>
                  <OptionsList
                    options={question?.options}
                    onOptionChange={handleOptionChange}
                    onOptionSelect={
                      !isSurvey ? handleCorrectOptionChange : undefined
                    }
                    onOptionRemove={removeOption}
                    onOptionsReorder={handleOptionReorder}
                    disabled={disabled}
                    language={language}
                    isSurvey={isSurvey}
                    radioName={`correct-answer-${question.id}`} // Added radioName prop
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addOption}
                    className="mt-2 w-full"
                    disabled={disabled}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {language === "ar" ? "إضافة خيار" : "Add Option"}
                  </Button>
                </>
              ) : question?.type === QuestionType.SINGLE_CHOICE ? (
                <div className="space-y-2">
                  {question?.options.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center gap-2 space-x-reverse border rounded-md p-3 overflow-auto"
                    >
                      <input
                        type="radio"
                        name={`answer-${question?.id}`}
                        value={option.id}
                        id={option.id}
                        checked={selectedAnswer?.includes(option.id)}
                        onChange={() => onAnswerSelect([option.id])}
                        className="w-4 h-4"
                        disabled={isShowMode}
                      />
                      <Label
                        htmlFor={option.id}
                        className="mr-2 flex-1 flex items-center gap-2"
                      >
                        {option.text}

                        {isShowMode && isSurvey ? (
                          <Avatar.Group
                            max={{
                              count: 5,
                              style: {
                                color: "#2563eb",
                                backgroundColor: "#dbeafe",
                              },
                              popover: {
                                placement: "top",
                                overlayStyle: {
                                  zIndex: 99999,
                                  pointerEvents: "auto",
                                },
                                overlayClassName: "avatar-overflow-row",
                              },
                            }}
                          >
                            {option.answers.map(({ user }) => {
                              const url =
                                user?.role === "student"
                                  ? "/admin/settings"
                                  : user?.role === "teacher" &&
                                    "/admin/teacher-settings";

                              return (
                                <Tooltip title={user?.name} placement="top">
                                  <Link to={url} state={{ userData: user }}>
                                    <Avatar
                                      src={`${UrlImage}${user?.image}`}
                                      className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                                        "bg-blue-100 text-blue-600",
                                        "dark:bg-blue-900 dark:text-blue-300"
                                      )}
                                    >
                                      {user?.name.substring(0, 2).toUpperCase()}
                                    </Avatar>
                                  </Link>
                                </Tooltip>
                              );
                            })}
                          </Avatar.Group>
                        ) : null}
                      </Label>
                    </div>
                  ))}
                </div>
              ) : question?.type === QuestionType.SURVEY_MULTIPLE_CHOICE ? (
                <div className="space-y-2">
                  {question?.options.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center gap-2 space-x-reverse border rounded-md p-3 overflow-auto"
                    >
                      <input
                        type="checkbox"
                        value={option.id}
                        id={option.id}
                        checked={selectedAnswer?.includes(option.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newSelected = checked
                            ? [...(selectedAnswer || []), option.id]
                            : (selectedAnswer || []).filter(
                                (id) => id !== option.id
                              );
                          onAnswerSelect(newSelected);
                        }}
                        className="w-4 h-4"
                        disabled={isShowMode}
                      />
                      <Label
                        htmlFor={option.id}
                        className="mr-2 flex-1 flex items-center gap-2"
                      >
                        {option.text}

                        {isShowMode && isSurvey ? (
                          <Avatar.Group
                            max={{
                              count: 5,
                              style: {
                                color: "#2563eb",
                                backgroundColor: "#dbeafe",
                              },
                              popover: {
                                placement: "top",
                                overlayStyle: {
                                  zIndex: 99999,
                                  pointerEvents: "auto",
                                },
                                overlayClassName: "avatar-overflow-row",
                              },
                            }}
                          >
                            {option.answers.map(({ user }) => {
                              const url =
                                user?.role === "student"
                                  ? "/admin/settings"
                                  : user?.role === "teacher" &&
                                    "/admin/teacher-settings";

                              return (
                                <Tooltip title={user?.name} placement="top">
                                  <Link to={url} state={{ userData: user }}>
                                    <Avatar
                                      src={`${UrlImage}${user?.image}`}
                                      className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                                        "bg-blue-100 text-blue-600",
                                        "dark:bg-blue-900 dark:text-blue-300"
                                      )}
                                    >
                                      {user?.name.substring(0, 2).toUpperCase()}
                                    </Avatar>
                                  </Link>
                                </Tooltip>
                              );
                            })}
                          </Avatar.Group>
                        ) : null}
                      </Label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {question?.options.map((option) => (
                    <div
                      key={option.id}
                      className={cn(
                        "flex items-center gap-2 space-x-reverse border rounded-md p-3",
                        showCorrectAnswers &&
                          option.isCorrect &&
                          "bg-green-500/20 border-green-300",
                        showCorrectAnswers &&
                          selectedAnswer === option.id &&
                          !option.isCorrect &&
                          "bg-red-50 border-red-300 text-black"
                      )}
                    >
                      <input
                        type="radio"
                        name={`answer-${question?.id}`}
                        value={option.id}
                        id={option.id}
                        checked={selectedAnswer === option.id}
                        onChange={() => onAnswerSelect([option.id])}
                        className="w-4 h-4"
                        disabled={isShowMode}
                      />
                      <Label htmlFor={option.id} className="mr-2 flex-1">
                        {option.text}
                      </Label>
                      {showCorrectAnswers && option.isCorrect && (
                        <span className="text-green-600 text-sm flex items-center">
                          <Check className="h-4 w-4 mx-1" />
                          {language === "ar"
                            ? "الإجابة الصحيحة"
                            : "Correct Answer"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {question?.type === QuestionType.TRUE_FALSE && (
            <div className="space-y-2">
              {isEditMode ? (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-500">
                      {language === "ar" ? "الخيارات" : "Options"}
                    </div>
                    <div className="text-sm font-medium text-green-600">
                      {language === "ar" ? "الإجابة الصحيحة" : "Correct Answer"}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 space-x-reverse border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`correct-answer-${question?.id}`}
                        value={question?.options[0]?.id || ""}
                        id={`true-${question?.id}`}
                        checked={question?.options[0]?.isCorrect}
                        onChange={() =>
                          handleCorrectOptionChange(question?.options[0]?.id)
                        }
                        disabled={disabled}
                        className="w-4 h-4"
                      />
                      <Label htmlFor={`true-${question?.id}`} className="mr-2">
                        {language === "ar" ? "صح" : "True"}
                      </Label>
                    </div>
                    {question?.options[0]?.isCorrect && (
                      <span className="text-green-600 text-sm flex items-center">
                        <Check className="h-4 w-4 mx-1" />
                        {language === "ar"
                          ? "الإجابة الصحيحة"
                          : "Correct Answer"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 space-x-reverse border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`correct-answer-${question?.id}`}
                        value={question?.options[1]?.id || ""}
                        id={`false-${question?.id}`}
                        checked={question?.options[1]?.isCorrect}
                        onChange={() =>
                          handleCorrectOptionChange(question?.options[1]?.id)
                        }
                        disabled={disabled}
                        className="w-4 h-4"
                      />
                      <Label htmlFor={`false-${question?.id}`} className="mr-2">
                        {language === "ar" ? "خطأ" : "False"}
                      </Label>
                    </div>
                    {question?.options[1]?.isCorrect && (
                      <span className="text-green-600 text-sm flex items-center">
                        <Check className="h-4 w-4 mx-1" />
                        {language === "ar"
                          ? "الإجابة الصحيحة"
                          : "Correct Answer"}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  {question?.options.map((option) => (
                    <div
                      key={option.id}
                      className={cn(
                        "flex items-center gap-2 space-x-reverse border rounded-md p-3",
                        showCorrectAnswers &&
                          option.isCorrect &&
                          "bg-green-500/20 border-green-300",
                        showCorrectAnswers &&
                          selectedAnswer === option.id &&
                          !option.isCorrect &&
                          "bg-red-50 border-red-300 text-black"
                      )}
                    >
                      <input
                        type="radio"
                        name={`answer-${question?.id}`}
                        value={option.id}
                        id={option.id}
                        checked={selectedAnswer === option.id}
                        onChange={() => onAnswerSelect([option.id])}
                        className="w-4 h-4"
                        disabled={isShowMode}
                      />
                      <Label htmlFor={option.id} className="mr-2 flex-1">
                        {option.text}
                      </Label>
                      {showCorrectAnswers && option.isCorrect && (
                        <span className="text-green-600 text-sm flex items-center">
                          <Check className="h-4 w-4 mx-1" />
                          {language === "ar"
                            ? "الإجابة الصحيحة"
                            : "Correct Answer"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {question?.type === QuestionType.PARAGRAPH && (
            <div>
              {isEditMode ? (
                <div className="bg-muted/50 p-4 rounded-md text-center text-muted-foreground">
                  {language === "ar"
                    ? "سيتمكن الطالب من كتابة إجابة نصية طويلة هنا"
                    : "Student will be able to write a long text answer here"}
                </div>
              ) : (
                <Textarea
                  placeholder={
                    language === "ar"
                      ? "اكتب إجابتك هنا..."
                      : "Write your answer here..."
                  }
                  className="w-full h-24"
                  readOnly={isShowMode}
                  value={question?.answer}
                  onChange={handleParagraphAnswerChange}
                />
              )}
            </div>
          )}

          {question?.type === QuestionType.FILE_UPLOAD && (
            <div>
              {isEditMode ? (
                <div className="bg-muted/50 p-4 rounded-md text-center text-muted-foreground">
                  {language === "ar"
                    ? "سيتمكن الطالب من تحميل ملف هنا"
                    : "Student will be able to upload a file here"}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                  <Button variant="outline">
                    <FileUp className="h-4 w-4 mr-2" />
                    {language === "ar" ? "تحميل ملف" : "Upload File"}
                  </Button>
                  <p className="text-sm text-gray-500 mt-2">
                    {language === "ar"
                      ? "أو اسحب الملف وأفلته هنا"
                      : "Or drag and drop file here"}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
